import React, { useEffect, useMemo, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Send, Monitor, Smartphone, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Phase 3G — Sales Pitch Studio.
 *
 * Consumes the canonical /pitch/{type}/context + /preview + /send endpoints.
 * The preview HTML rendered here is the SAME payload the send endpoint will
 * transmit — there is no client-side email rendering.
 *
 * Only pitch_type ∈ {"website", "optimise"}. CRM is never automatically
 * pitched.
 */

const TITLE_BY_TYPE = {
  website:  "WEBSITE SALES PITCH",
  optimise: "ORANGE OPTIMISE SALES PITCH",
  crm:      "CRM & BUSINESS SYSTEMS PITCH",
  full:     "FULL ORANGEBYSITE PITCH",
};

const EYEBROW_BY_TYPE = {
  website:  "Website Pitch",
  optimise: "Orange Optimise Pitch",
  crm:      "CRM Pitch",
  full:     "Full Pitch",
};

const SCORE_TONE = {
  excellent:       { fg: "#0F7B3E", bg: "#E7F7EE", label: "Excellent" },
  good:            { fg: "#0F7B3E", bg: "#E7F7EE", label: "Good" },
  needs_attention: { fg: "#8A5A00", bg: "#FFF7E6", label: "Needs attention" },
  poor:            { fg: "#8B2A0F", bg: "#FDEBE3", label: "Poor" },
  unknown:         { fg: "#4A4A4A", bg: "#F6F5F2", label: "—" },
};


export default function SalesPitchModal({ open, onClose, businessId, pitchType, onSent }) {
  const [phase, setPhase] = useState("loading"); // loading | ready | sending | sent | error
  const [ctx, setCtx] = useState(null);
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [device, setDevice] = useState("desktop");
  const [error, setError] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);
  const [followUp, setFollowUp] = useState(false);

  const title = TITLE_BY_TYPE[pitchType] || "SALES PITCH";
  const eyebrow = EYEBROW_BY_TYPE[pitchType] || "Pitch";

  useEffect(() => {
    if (!open) return;
    setPhase("loading"); setError(null); setPreview(null);
    setSubject(""); setIntro(""); setSentInfo(null); setFollowUp(false);
    hqApi.get(`/control/clients/${businessId}/pitch/${pitchType}/context`)
      .then((r) => {
        setCtx(r.data);
        setSubject(r.data?.default_subject || "");
        setPhase("ready");
      })
      .catch((e) => {
        setError(formatApiError(e));
        setPhase("error");
      });
  }, [open, businessId, pitchType]);

  // Keep preview in sync when subject/intro/context change.
  useEffect(() => {
    if (phase !== "ready" || !ctx) return;
    let cancelled = false;
    hqApi.post(`/control/clients/${businessId}/pitch/${pitchType}/preview`, {
      subject: subject || undefined,
      intro:   intro   || undefined,
      contact_id: ctx?.contact?.id || undefined,
    }).then((r) => { if (!cancelled) setPreview(r.data); })
      .catch(() => { /* silent — keep last preview */ });
    return () => { cancelled = true; };
  }, [phase, ctx, businessId, pitchType, subject, intro]);

  const handleSend = async () => {
    if (!ctx || phase === "sending") return;
    if (ctx.within_duplicate_window && !followUp) {
      setFollowUp(true);
      toast.warning(`Same pitch was sent within ${ctx.duplicate_window_days} days. Click "SEND FOLLOW-UP" to confirm.`);
      return;
    }
    setPhase("sending");
    try {
      const r = await hqApi.post(`/control/clients/${businessId}/pitch/${pitchType}/send`, {
        subject: subject || undefined,
        intro:   intro   || undefined,
        contact_id: ctx?.contact?.id || undefined,
        follow_up: !!followUp,
      });
      const status = r.data?.status;
      if (status === "sent") {
        setSentInfo(r.data);
        setPhase("sent");
        toast.success("Pitch sent");
        if (onSent) onSent(r.data);
      } else if (status === "skipped") {
        setPhase("ready");
        toast.warning("Send skipped — email provider not configured (Resend key missing)");
      } else {
        setPhase("ready");
        toast.error("Send failed — see communications tab");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "duplicate_pitch") {
        setFollowUp(true);
        toast.warning(detail.message || "Pitch was sent recently — confirm follow-up.");
      } else {
        toast.error(formatApiError(e));
      }
      setPhase("ready");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="sales-pitch-modal"
      className="fixed inset-0 z-[9000] flex items-stretch md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#F6F5F2] w-full h-full md:h-auto md:max-h-[92vh] md:max-w-[1180px] md:rounded-2xl overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-7 py-4 border-b border-[#EAEAEA] bg-white">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{eyebrow}</div>
            <div className="mt-0.5 text-[16px] md:text-lg font-black tracking-[-0.01em]">{title}</div>
            <div className="text-[11px] text-[#8A8A8A]">
              Personalised for <strong className="text-[#0A0A0A]">{ctx?.business?.name || "…"}</strong>
            </div>
          </div>
          <button
            data-testid="sales-pitch-close"
            type="button" onClick={onClose}
            className="text-[#4A4A4A] hover:text-black p-2 rounded-full"
          ><X size={18}/></button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {phase === "loading" && (
            <div className="p-10 text-center text-[#8A8A8A] text-sm" data-testid="sales-pitch-loading">
              Loading real scan data…
            </div>
          )}
          {phase === "error" && (
            <div className="p-10 text-center" data-testid="sales-pitch-error">
              <AlertTriangle className="inline text-[#C1350B]" size={26}/>
              <div className="mt-3 font-semibold text-[#C1350B]">{error || "Unable to build pitch"}</div>
              <p className="mt-2 text-sm text-[#4A4A4A]">
                A recent completed Orange Optimise scan is required before a pitch can be built.
              </p>
            </div>
          )}
          {phase === "sent" && (
            <SentSuccess
              info={sentInfo}
              contactEmail={ctx?.contact?.email}
              onViewComm={() => {
                onClose();
                // Trigger comms tab open — HQ profile handles this via URL hash.
                window.location.hash = "#comms";
              }}
              onClose={onClose}
            />
          )}
          {(phase === "ready" || phase === "sending") && ctx && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px]">
              {/* LEFT — preview */}
              <div className="p-4 md:p-6 border-b lg:border-b-0 lg:border-r border-[#EAEAEA] bg-[#F0EEEA]">
                <div className="flex items-center gap-2 mb-3">
                  <button
                    data-testid="sales-pitch-device-desktop"
                    onClick={() => setDevice("desktop")}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase inline-flex items-center gap-1 ${device === "desktop" ? "bg-black text-white" : "bg-white border border-[#EAEAEA] text-[#4A4A4A]"}`}
                  ><Monitor size={12}/> Desktop</button>
                  <button
                    data-testid="sales-pitch-device-mobile"
                    onClick={() => setDevice("mobile")}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase inline-flex items-center gap-1 ${device === "mobile" ? "bg-black text-white" : "bg-white border border-[#EAEAEA] text-[#4A4A4A]"}`}
                  ><Smartphone size={12}/> Mobile</button>
                  <div className="ml-auto text-[10px] text-[#8A8A8A]">Preview matches send exactly</div>
                </div>
                <div className="mx-auto bg-white rounded-xl shadow-sm border border-[#EAEAEA] overflow-hidden" style={{ width: device === "mobile" ? 390 : "100%", maxWidth: device === "desktop" ? 700 : 390 }}>
                  <iframe
                    data-testid="sales-pitch-preview-iframe"
                    title="Pitch email preview"
                    srcDoc={preview?.html || "<p style='padding:24px;font-family:sans-serif;color:#8A8A8A;'>Building preview…</p>"}
                    style={{ width: "100%", height: device === "mobile" ? 760 : 720, border: 0, background: "#F6F5F2" }}
                  />
                </div>
              </div>

              {/* RIGHT — details/controls */}
              <div className="p-4 md:p-6 bg-white flex flex-col gap-4">
                <SectionLabel>Recipient</SectionLabel>
                <div className="text-sm">
                  <div className="font-semibold">{ctx?.contact?.name || "—"}</div>
                  <div className="text-[#4A4A4A] break-all" data-testid="sales-pitch-recipient">{ctx?.contact?.email || <span className="text-[#C1350B]">No email on file</span>}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <MetaCard label="Pitch">
                    <span className="font-black">{pitchType === "website" ? "WEBSITE" : pitchType === "crm" ? "CRM" : pitchType === "full" ? "FULL" : "OPTIMISE"}</span>
                  </MetaCard>
                  <MetaCard label="Orange Score">
                    {(pitchType === "crm" || pitchType === "full")
                      ? <span className="text-[#8A8A8A]">Not applicable</span>
                      : <ScoreBadge score={ctx?.model?.orange_score} band={ctx?.model?.score_band}/>}
                  </MetaCard>
                </div>

                {ctx?.model?.strengths?.length > 0 && (
                  <div>
                    <SectionLabel>Strengths (from scan)</SectionLabel>
                    <ul className="text-[13px] mt-1 space-y-0.5" data-testid="sales-pitch-strengths">
                      {ctx.model.strengths.map((s) => (
                        <li key={s.key}><span className="text-[#0FA958] font-black">✓</span> {s.label} <span className="text-[#8A8A8A]">· {s.score}/100</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {ctx?.model?.weaknesses?.length > 0 && (
                  <div>
                    <SectionLabel>Weakest areas</SectionLabel>
                    <ul className="text-[13px] mt-1 space-y-0.5" data-testid="sales-pitch-weaknesses">
                      {ctx.model.weaknesses.map((w) => (
                        <li key={w.key}><span className="text-[#F0A020] font-black">!</span> {w.label} <span className="text-[#8A8A8A]">· {w.score}/100</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <SectionLabel>Subject</SectionLabel>
                  <input
                    data-testid="sales-pitch-subject-input"
                    type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-sm focus:outline-none focus:border-[#FF6A00]"
                    maxLength={200}
                  />
                </div>

                <div>
                  <SectionLabel>Personal intro <span className="normal-case font-normal text-[#8A8A8A] tracking-normal">(optional — replaces default greeting)</span></SectionLabel>
                  <textarea
                    data-testid="sales-pitch-intro-input"
                    value={intro} onChange={(e) => setIntro(e.target.value)}
                    rows={3} maxLength={1000}
                    placeholder="Leave blank to use the default intro"
                    className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-sm focus:outline-none focus:border-[#FF6A00]"
                  />
                </div>

                {ctx?.within_duplicate_window && ctx?.last_pitch && (
                  <div className="p-3 rounded-lg border border-[#F3D89A] bg-[#FFF7E6]" data-testid="sales-pitch-duplicate-warning">
                    <div className="text-[10px] tracking-[0.20em] uppercase font-black text-[#8A5A00]">Already sent</div>
                    <div className="mt-1 text-[12px] text-[#4A4A4A]">
                      This pitch was sent on {new Date(ctx.last_pitch.sent_at).toLocaleDateString()} — within the {ctx.duplicate_window_days}-day window.
                    </div>
                    <label className="mt-2 inline-flex items-center gap-2 text-[12px]">
                      <input
                        type="checkbox" checked={followUp}
                        onChange={(e) => setFollowUp(e.target.checked)}
                        data-testid="sales-pitch-followup-checkbox"
                      />
                      Send as deliberate follow-up
                    </label>
                  </div>
                )}

                <div className="mt-auto pt-2 flex flex-col gap-2">
                  <button
                    data-testid="sales-pitch-send"
                    onClick={handleSend}
                    disabled={phase === "sending" || !ctx?.contact?.email}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={13}/>
                    {phase === "sending"
                      ? "Sending…"
                      : (ctx?.within_duplicate_window && followUp ? "Send follow-up" : "Send pitch")}
                  </button>
                  <div className="text-center text-[10px] text-[#8A8A8A] tracking-[0.14em] uppercase">
                    Uses Resend · SENT means accepted, not delivered
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SectionLabel({ children }) {
  return <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">{children}</div>;
}

function MetaCard({ label, children }) {
  return (
    <div className="p-3 rounded-lg border border-[#EAEAEA] bg-[#F6F5F2]">
      <div className="text-[9px] tracking-[0.24em] uppercase font-bold text-[#8A6D00]">{label}</div>
      <div className="mt-1 text-[13px]">{children}</div>
    </div>
  );
}

function ScoreBadge({ score, band }) {
  if (typeof score !== "number") return <span className="text-[#8A8A8A]">—</span>;
  const tone = SCORE_TONE[band] || SCORE_TONE.unknown;
  return (
    <span
      className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-[11px] font-black"
      style={{ color: tone.fg, background: tone.bg }}
      data-testid="sales-pitch-score-badge"
    >
      {score}/100 · {tone.label}
    </span>
  );
}

function SentSuccess({ info, contactEmail, onViewComm, onClose }) {
  return (
    <div className="p-8 md:p-14 text-center" data-testid="sales-pitch-sent">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E7F7EE] text-[#0F7B3E]">
        <CheckCircle2 size={32}/>
      </div>
      <div className="mt-4 text-[12px] tracking-[0.28em] uppercase font-black text-[#0F7B3E]">Pitch sent</div>
      <div className="mt-2 text-lg font-black">Accepted by Resend</div>
      <div className="mt-1 text-sm text-[#4A4A4A]">
        Sent to <strong>{contactEmail}</strong>{info?.provider_message_id ? <span> · id <span className="text-[10px] text-[#8A8A8A]">{info.provider_message_id}</span></span> : null}
      </div>
      <div className="mt-2 text-[11px] text-[#8A8A8A]">
        Delivery status will update automatically as Resend confirms delivery.
      </div>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <button
          data-testid="sales-pitch-view-comm"
          onClick={onViewComm}
          className="px-5 py-2 rounded-full bg-black text-white text-[10px] font-black tracking-[0.16em] uppercase hover:bg-[#222]"
        >
          <ExternalLink size={11} className="inline mr-1"/> View communication
        </button>
        <button
          data-testid="sales-pitch-close-success"
          onClick={onClose}
          className="px-5 py-2 rounded-full bg-white border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:bg-[#F6F5F2]"
        >Close</button>
      </div>
    </div>
  );
}
